import { useState, useEffect, useCallback } from "react";
import { outdatedReasonText } from "../../utils/mergeRequestStatus";
import { createPortal } from "react-dom";
import {
  X, Loader2, AlertTriangle, GitMerge, ChevronDown, ChevronRight, FileText, Shield, Check, GitCommit,
} from "lucide-react";
import type { MergePreviewResult, MergePreviewConflict, Branch } from "../../data/types";
import { useProject } from "../../contexts/ProjectContext";
import { useAuth } from "../../contexts/AuthContext";
import { authFetch } from "../../utils/authFetch";
import CommitDiffPanel from "./CommitDiffPanel";

/* ── Self-contained API helpers (no CmsProvider dependency) ── */

async function fetchMergePreview(projectSlug: string, branchId: string, targetId?: string, sourceCommitId?: string | null): Promise<MergePreviewResult> {
  const q = new URLSearchParams();
  if (targetId) q.set("targetBranchId", targetId);
  // A request that merges a rebased copy of the branch previews that commit.
  if (sourceCommitId) q.set("sourceCommitId", sourceCommitId);
  const params = q.toString() ? `?${q.toString()}` : "";
  const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${branchId}/merge-preview${params}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(typeof err.detail === "string" ? err.detail : err.detail?.message || `Merge preview failed: ${res.statusText}`);
  }
  return res.json();
}

async function fetchRebasePreview(projectSlug: string, branchId: string, targetBranchId?: string, targetCommitId?: string) {
  const body: Record<string, string> = {};
  if (targetBranchId) body.targetBranchId = targetBranchId;
  if (targetCommitId) body.targetCommitId = targetCommitId;
  const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${branchId}/rebase-preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(typeof err.detail === "string" ? err.detail : err.detail?.message || `Rebase preview failed: ${res.statusText}`);
  }
  return res.json();
}

/** An API refusal with its machine-readable code (detail.error). */
class ApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) { super(message); this.code = code; }
}

async function fetchRebaseApply(projectSlug: string, branchId: string, tempBranchId: string) {
  const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${branchId}/rebase-apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tempBranchId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new ApiError(
      typeof err.detail === "string" ? err.detail : err.detail?.message || `Rebase apply failed: ${res.statusText}`,
      typeof err.detail === "object" ? err.detail?.error : undefined,
    );
  }
  return res.json();
}

async function fetchRebaseCancel(projectSlug: string, branchId: string, tempBranchId: string) {
  await authFetch(`/api/projects/${projectSlug}/studio/branches/${branchId}/rebase-cancel`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tempBranchId }),
  }).catch(() => {});
}

async function fetchResetToDraft(projectSlug: string, branchId: string, targetBranchId?: string) {
  const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${branchId}/reset-to-draft`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(targetBranchId ? { targetBranchId } : {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(typeof err.detail === "string" ? err.detail : err.detail?.message || `Reset to draft failed: ${res.statusText}`);
  }
  return res.json();
}

async function fetchBranches(projectSlug: string): Promise<Branch[]> {
  const res = await authFetch(`/api/projects/${projectSlug}/studio/branches`);
  if (!res.ok) return [];
  return res.json();
}

async function fetchMergeInto(projectSlug: string, sourceBranchId: string, targetBranchId: string, strategy: "merge" | "overwrite" = "merge") {
  const res = await authFetch(`/api/projects/${projectSlug}/studio/merge-requests`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceBranchId, targetBranchId, strategy }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(typeof err.detail === "string" ? err.detail : err.detail?.message || `Merge failed: ${res.statusText}`);
  }
  return res.json();
}

async function fetchBranchCommits(projectSlug: string, branchId: string, head?: string | null): Promise<{ id: string; message: string; sequence: number; createdAt?: string; authorId?: string; parentId?: string }[]> {
  const headParam = head ? `&head=${encodeURIComponent(head)}` : "";
  const res = await authFetch(`/api/projects/${projectSlug}/studio/branches/${branchId}/commits?limit=500${headParam}`);
  if (!res.ok) return [];
  const data = await res.json();
  return (data.commits || []).map((c: any) => ({
    id: c.id,
    message: c.message || "",
    sequence: c.sequence ?? 0,
    createdAt: c.createdAt || c.created_at,
    authorId: c.authorId || c.author_id,
    parentId: c.parentId || c.parent_id,
  }));
}

/** Newest-first commits of `chain` above the fork point: stop at `forkId` or
 *  the first commit `other` also has. Set membership is not enough — both
 *  chains are capped newest-first, so the deeper window's oldest shared
 *  commits would look like its own. */
export function chainUntil<T extends { id: string }>(chain: T[], other: Set<string>, forkId?: string | null): T[] {
  const i = chain.findIndex(c => c.id === forkId || other.has(c.id));
  return i < 0 ? chain : chain.slice(0, i);
}

/* ── Main component ── */

interface Props {
  branchId: string;
  branchName: string;
  onClose: () => void;
  onMerged: () => void;
  /** When set, dialog is in merge-request review mode */
  mergeRequestId?: string;
  mergeRequestStatus?: "pending" | "rejected" | "merged" | "outdated" | "closed";
  /** Why the request went outdated (merge_requests.status_reason). */
  mergeRequestStatusReason?: string | null;
  /** Stored commit IDs for historical diff on completed MRs */
  sourceHeadAtCreation?: string | null;
  targetHeadAtCreation?: string | null;
  /** Branch sequences at MR creation */
  sourceSequence?: number | null;
  targetSequence?: number | null;
  /** Optional target branch for merge/rebase to non-main branches */
  targetBranchId?: string;
  targetBranchName?: string;
  /** Optional specific commit on the target branch to rebase onto */
  targetCommitId?: string;
  /** Mode: "merge" (default) merges source→target, "rebase" rebases source on top of target */
  mode?: "merge" | "rebase";
  /** Pull mode: like merge but source can't be mutated, always uses temp branch for rebase */
  pullMode?: boolean;
  /** Initial merge strategy from branch picker */
  initialStrategy?: "merge" | "overwrite";
  /** Who created the merge request (user ID) — for close/withdraw by owner */
  mergeRequestAuthor?: string;
  /** The request merges this rebased copy of the branch ("Keep source
   *  branch unchanged"), not the branch head: the preview, the commit list
   *  and Approve use this commit; Rebase / Reset to Draft (which move the
   *  branch) are not offered. */
  mergeHeadCommitId?: string | null;
  /** Called when user clicks Rebase in non-FF merge — parent should open RebaseSessionDialog */
  onRebase?: () => void;
}

export default function MergePreviewDialog({ branchId, branchName, onClose, onMerged, mergeRequestId, mergeRequestStatus, mergeRequestStatusReason, sourceHeadAtCreation, targetHeadAtCreation, sourceSequence: _sourceSequence, targetSequence: _targetSequence, targetBranchId: propTargetBranchId, targetBranchName: propTargetBranchName, targetCommitId: propTargetCommitId, mode = "merge", pullMode = false, initialStrategy, mergeRequestAuthor, mergeHeadCommitId, onRebase }: Props) {
  const { currentProject, isAdmin } = useProject();
  const { user } = useAuth();
  const projectSlug = currentProject?.slug || "";
  const isOwner = !!mergeRequestAuthor && !!user?.id && mergeRequestAuthor === user.id;
  const [branches, setBranches] = useState<Branch[]>([]);
  const [preview, setPreview] = useState<MergePreviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draftWarning, setDraftWarning] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);
  const [rebasing, setRebasing] = useState(false);
  const [resettingToDraft, setResettingToDraft] = useState(false);
  const [resetToDraftCompleted, setResetToDraftCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [closingMr, setClosingMr] = useState(false);
  const [conflictsExpanded, setConflictsExpanded] = useState(false);
  // The previewed rebase went stale (the target moved): "Preview again".
  const [stalePreview, setStalePreview] = useState(false);
  // Branch compare: the branch's own changes (vs the fork point) or the
  // two heads side by side.
  const [compareMode, setCompareMode] = useState<"fork" | "heads">("fork");
  const pinnedCopy = !!mergeRequestId && !!mergeHeadCommitId;

  const isMrReview = !!mergeRequestId && mergeRequestStatus !== "outdated";
  const isOutdatedMr = !!mergeRequestId && mergeRequestStatus === "outdated";
  const mrPending = mergeRequestStatus === "pending";

  const [sourceCommitId, setSourceCommitId] = useState<string | null>(null);
  const [targetCommitId, setTargetCommitId] = useState<string | null>(null);
  const [hasUncommittedChanges, setHasUncommittedChanges] = useState(false);
  const [rebaseCompleted, setRebaseCompleted] = useState(false);
  const [alreadyUpToDate, setAlreadyUpToDate] = useState(false);
  const [nothingToMerge, setNothingToMerge] = useState(false);
  // "source" = mutate source branch, "temp" = merge temp branch directly
  const [rebaseStrategy] = useState<"source" | "temp" | null>(null);

  // Per-commit diff: when set, tree diff shows this commit's changes instead of overall branch diff
  // branchOverride: if set, use this branch for the diff API call instead of the default
  const [selectedCommitForDiff, setSelectedCommitForDiff] = useState<{ id: string; parentId: string | null; message: string; branchOverride?: string } | null>(null);

  // Merge strategy: "merge" (default fast-forward) or "overwrite" (replace target entirely)
  const [mergeStrategy, setMergeStrategy] = useState<"merge" | "overwrite">(initialStrategy || "merge");

  // Overwrite mode: tab switch for left pane (source vs target commit chain)
  const [overwriteTab, setOverwriteTab] = useState<"source" | "target">("source");

  // Unified commit chain: source-only, target-only, and common ancestor commits
  const [commitChain, setCommitChain] = useState<{
    sourceCommits: { id: string; message: string; sequence: number; createdAt?: string; parentId?: string }[];
    targetCommits: { id: string; message: string; sequence: number; createdAt?: string; parentId?: string }[];
    commonCommits: { id: string; message: string; sequence: number; createdAt?: string; parentId?: string }[];
    allSourceCommits: { id: string; message: string; sequence: number; createdAt?: string; parentId?: string }[];
    allTargetCommits: { id: string; message: string; sequence: number; createdAt?: string; parentId?: string }[];
    lcaId: string | null;
  }>({ sourceCommits: [], targetCommits: [], commonCommits: [], allSourceCommits: [], allTargetCommits: [], lcaId: null });

  // Rebase preview state (two-step rebase flow)
  const [rebasePreviewData, setRebasePreviewData] = useState<{
    tempBranchId: string;
    oldCommits: any[];
    newCommits: any[];
    conflicts: any[];
    status: string;
    forkPointId?: string | null;
  } | null>(null);

  // Is this a completed MR where we show historical diff?
  const isCompletedMr = isMrReview && (mergeRequestStatus === "merged" || mergeRequestStatus === "rejected" || mergeRequestStatus === "closed");

  // Determine if target branch is protected
  const targetBranch = preview?.targetBranchId
    ? branches.find(b => b.id === preview.targetBranchId)
    : propTargetBranchId ? branches.find(b => b.id === propTargetBranchId) : null;
  const isProtected = targetBranch?.isProtected ?? false;
  const targetBranchName = propTargetBranchName || targetBranch?.name || "main";
  const isRebaseMode = mode === "rebase";

  // Fetch branches for protected-branch detection
  useEffect(() => {
    if (!projectSlug) return;
    let cancelled = false;
    (async () => {
      const b = await fetchBranches(projectSlug);
      if (!cancelled) setBranches(b);
    })();
    return () => { cancelled = true; };
  }, [projectSlug]);

  // Auto-trigger rebase preview ONLY in explicit rebase mode (not merge mode)
  // In merge mode, the user clicks "Rebase & Merge" which opens RebaseSessionDialog via onRebase callback
  useEffect(() => {
    // Disabled — rebase is now handled by RebaseSessionDialog, not inline
    // Only keep for explicit rebase mode if needed in future
  }, []);

  // Fetch merge preview
  useEffect(() => {
    if (!projectSlug) return;
    let cancelled = false;
    setLoading(true);

    // For completed MRs, use stored commit IDs to show historical diff
    if (isCompletedMr && sourceHeadAtCreation && targetHeadAtCreation) {
      setSourceCommitId(sourceHeadAtCreation);
      setTargetCommitId(targetHeadAtCreation);
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const prev = await fetchMergePreview(projectSlug, branchId, propTargetBranchId, pinnedCopy ? mergeHeadCommitId : null);
        if (cancelled) return;
        setPreview(prev);
        setSourceCommitId(prev.sourceHead || null);
        setTargetCommitId(propTargetCommitId || prev.targetHead || null);
        if ((prev as any).nothingToMerge) {
          setNothingToMerge(true);
        }
        if ((prev as any).draftWarning) {
          setDraftWarning((prev as any).draftWarning.message);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load merge preview");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [branchId, projectSlug]);

  // Unified commit chain: fetch both branches, find LCA, split into sections
  useEffect(() => {
    if (!preview || !projectSlug || isCompletedMr) {
      setCommitChain({ sourceCommits: [], targetCommits: [], commonCommits: [], allSourceCommits: [], allTargetCommits: [], lcaId: null });
      return;
    }
    const targetBrId = propTargetBranchId || preview.targetBranchId;
    if (!targetBrId) return;
    let cancelled = false;
    (async () => {
      try {
        const [srcCommits, tgtCommits] = await Promise.all([
          fetchBranchCommits(projectSlug, branchId, pinnedCopy ? mergeHeadCommitId : null),
          fetchBranchCommits(projectSlug, targetBrId),
        ]);
        if (cancelled) return;

        const srcIds = new Set(srcCommits.map(c => c.id));
        const tgtIds = new Set(tgtCommits.map(c => c.id));

        // LCA: take the server's merge base. Both lists are the newest 500, so
        // once the fork point is older than that they share nothing; the
        // first shared commit is only the fallback, for a missing or empty
        // lcaCommitId.
        const p = preview as any;
        const mergeBase: string | null = p.nothingToMerge ? p.sourceHead : p.fastForward ? p.targetHead : p.lcaCommitId;
        const sourceOnly = chainUntil(srcCommits, tgtIds, mergeBase);
        const lcaId = mergeBase || srcCommits[sourceOnly.length]?.id || null;

        // Target-only: commits in target chain before LCA (not in source)
        let relevantTarget = tgtCommits;
        if (propTargetCommitId) {
          const idx = tgtCommits.findIndex(c => c.id === propTargetCommitId);
          if (idx >= 0) relevantTarget = tgtCommits.slice(idx);
        }
        const targetOnly = chainUntil(relevantTarget, srcIds, mergeBase);

        // Common: from LCA onward (shared ancestors)
        const lcaIdx = srcCommits.findIndex(c => c.id === lcaId);
        const common = lcaIdx >= 0 ? srcCommits.slice(lcaIdx, lcaIdx + 5) : []; // limit to 5

        setCommitChain({ sourceCommits: sourceOnly, targetCommits: targetOnly, commonCommits: common, allSourceCommits: srcCommits, allTargetCommits: tgtCommits, lcaId });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [preview, projectSlug, branchId, propTargetBranchId, propTargetCommitId, isCompletedMr]);

  // Compute the commitA/commitB/branchId for the right-panel CommitDiffPanel
  // Branch compare shows what the merge brings: the branch against the
  // point it forked from the target. Diffing the two heads counted the
  // target's own newer edits as changes of the branch.
  const forkPointId = (preview as any)?.lcaCommitId as string | undefined;
  const compareFromFork = compareMode === "fork" && !!forkPointId && !isCompletedMr && !propTargetCommitId;
  const diffPanelCommitA = (() => {
    if (selectedCommitForDiff) {
      return selectedCommitForDiff.parentId;
    }
    if (rebasePreviewData) {
      return targetCommitId;
    }
    return compareFromFork ? forkPointId! : targetCommitId;
  })();

  const diffPanelCommitB = (() => {
    if (selectedCommitForDiff) {
      return selectedCommitForDiff.id;
    }
    if (rebasePreviewData) {
      const newCommits = rebasePreviewData.newCommits || [];
      return newCommits.length > 0 ? newCommits[0].id : null;
    }
    return sourceCommitId;
  })();

  const diffPanelBranchId = (() => {
    if (selectedCommitForDiff) {
      if (selectedCommitForDiff.branchOverride) return selectedCommitForDiff.branchOverride;
      if (rebasePreviewData?.tempBranchId) {
        const newIds = new Set((rebasePreviewData.newCommits || []).map((c: any) => c.id));
        if (newIds.has(selectedCommitForDiff.id)) return rebasePreviewData.tempBranchId;
      }
      return branchId;
    }
    if (rebasePreviewData) return rebasePreviewData.tempBranchId;
    return branchId;
  })();

  // Direct merge (non-protected)
  const handleDirectMerge = useCallback(async () => {
    const resolvedTargetId = propTargetBranchId || preview?.targetBranchId;
    if (!resolvedTargetId) { setError("No target branch"); return; }
    setMerging(true); setError(null);
    try {
      // If "keep source intact" strategy, merge the temp branch instead
      const sourceBranch = rebaseStrategy === "temp" && rebasePreviewData?.tempBranchId
        ? rebasePreviewData.tempBranchId
        : branchId;
      await fetchMergeInto(projectSlug, sourceBranch, resolvedTargetId, mergeStrategy);
      onMerged();
    } catch (e: any) { setError(e.message || "Merge failed"); }
    finally { setMerging(false); }
  }, [branchId, projectSlug, onMerged, propTargetBranchId, preview?.targetBranchId, rebaseStrategy, rebasePreviewData, mergeStrategy]);

  // Submit merge request (protected)
  const handleSubmitForReview = useCallback(async () => {
    if (!preview?.targetBranchId) return;
    setSubmitting(true); setError(null);
    try {
      const res = await authFetch(`/api/projects/${projectSlug}/studio/merge-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceBranchId: branchId, targetBranchId: preview.targetBranchId, strategy: mergeStrategy }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to submit merge request");
      }
      setSubmitted(true);
      // Show success message for 2.5s so user clearly sees it
      setTimeout(() => onClose(), 2500);
    } catch (e: any) { setError(e.message || "Failed to submit"); }
    finally { setSubmitting(false); }
  }, [branchId, preview?.targetBranchId, projectSlug, onClose, mergeStrategy]);

  // Step 1: Preview rebase — creates temp branch, shows old vs new commits
  const handleRebasePreview = useCallback(async () => {
    setRebasing(true); setError(null); setStalePreview(false);
    // A previous (stale) preview's temp branch goes first.
    if (rebasePreviewData?.tempBranchId) {
      await fetchRebaseCancel(projectSlug, branchId, rebasePreviewData.tempBranchId);
    }
    setRebasePreviewData(null);
    try {
      const data = await fetchRebasePreview(projectSlug, branchId, propTargetBranchId, propTargetCommitId);
      if (data.status === "up_to_date") {
        // Selected commit is already an ancestor — nothing to rebase
        setAlreadyUpToDate(true);
      } else {
        setRebasePreviewData(data);
      }
    } catch (e: any) { setError(e.message || "Rebase preview failed"); }
    finally { setRebasing(false); }
  }, [branchId, projectSlug, propTargetBranchId, propTargetCommitId, rebasePreviewData]);

  // Step 2: Apply rebase — swaps real branch to rebased state
  const handleRebaseApply = useCallback(async () => {
    if (!rebasePreviewData?.tempBranchId) return;
    setRebasing(true); setError(null);
    try {
      await fetchRebaseApply(projectSlug, branchId, rebasePreviewData.tempBranchId);
      setRebasePreviewData(null);
      // Check for uncommitted changes after rebase
      try {
        const stagingRes = await authFetch(`/api/projects/${projectSlug}/studio/nodes/staged?branch=${branchId}`);
        if (stagingRes.ok) {
          const stagingData = await stagingRes.json();
          setHasUncommittedChanges((stagingData.stagedChanges || []).length > 0);
        }
      } catch { /* ignore */ }
      // Re-fetch preview after rebase
      const prev = await fetchMergePreview(projectSlug, branchId, propTargetBranchId);
      setPreview(prev);
      setSourceCommitId(prev.sourceHead || null);
      setTargetCommitId(prev.targetHead || null);
      setRebaseCompleted(true);
    } catch (e: any) {
      setError(e.message || "Rebase apply failed");
      // The target moved since the preview: nothing was applied; offer a
      // fresh preview onto its current head.
      if (e instanceof ApiError && (e.code === "target_changed" || e.code === "source_changed")) setStalePreview(true);
    }
    finally { setRebasing(false); }
  }, [branchId, projectSlug, rebasePreviewData, propTargetBranchId]);

  // Cancel rebase preview — clean up temp branch
  const handleRebaseCancel = useCallback(async () => {
    if (rebasePreviewData?.tempBranchId) {
      await fetchRebaseCancel(projectSlug, branchId, rebasePreviewData.tempBranchId);
    }
    setRebasePreviewData(null);
  }, [branchId, projectSlug, rebasePreviewData]);

  // Reset to draft — discard commits, keep changes as uncommitted drafts
  const handleResetToDraft = useCallback(async () => {
    setResettingToDraft(true); setError(null);
    try {
      // Cancel any active rebase preview first
      if (rebasePreviewData?.tempBranchId) {
        await fetchRebaseCancel(projectSlug, branchId, rebasePreviewData.tempBranchId);
        setRebasePreviewData(null);
      }
      await fetchResetToDraft(projectSlug, branchId, propTargetBranchId);
      setResetToDraftCompleted(true);
      onMerged(); // refresh parent
    } catch (e: any) { setError(e.message || "Reset to draft failed"); }
    finally { setResettingToDraft(false); }
  }, [branchId, projectSlug, propTargetBranchId, rebasePreviewData, onMerged]);

  // Wrap onClose to clean up temp branch if needed
  const handleClose = useCallback(() => {
    if (rebasePreviewData?.tempBranchId) {
      fetchRebaseCancel(projectSlug, branchId, rebasePreviewData.tempBranchId);
    }
    onClose();
  }, [onClose, branchId, projectSlug, rebasePreviewData]);

  // Approve merge request (admin)
  const handleApproveMr = useCallback(async () => {
    if (!mergeRequestId) return;
    setApproving(true); setError(null);
    try {
      // The server merges exactly what was requested and asks before
      // anything more: commits added after the request, or an overwrite
      // that drops target commits. Each confirmation retries with its flag.
      const flags: Record<string, boolean> = {};
      for (let attempt = 0; attempt < 3; attempt++) {
        const res = await authFetch(`/api/projects/${projectSlug}/studio/merge-requests/${mergeRequestId}/approve`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(flags),
        });
        if (res.ok) { onMerged(); return; }
        const err = await res.json().catch(() => ({}));
        const d = err.detail;
        const list = (cs: any[] | undefined) =>
          (cs || []).slice(0, 10).map((c) => `• ${c.message || c.id?.slice(0, 8)}`).join("\n");
        if (res.status === 409 && d?.error === "source_changed" && !flags.acceptCurrentHead) {
          const added: any[] = d.newCommits || [];
          const copies = added.length > 0 && added.every((c) => c.rebasedCopy);
          const why = copies ? " (rebased copies of the reviewed commits)" : "";
          if (!window.confirm(`The branch has ${added.length || "new"} commit(s) added after this merge request was created${why}:\n\n${list(added)}\n\nMerge them too?`)) return;
          flags.acceptCurrentHead = true;
          continue;
        }
        if (res.status === 409 && d?.error === "overwrite_drops_commits" && !flags.confirmOverwrite) {
          if (!window.confirm(`Overwriting removes these commits from the target branch:\n\n${list(d.droppedCommits)}\n\nOverwrite anyway?`)) return;
          flags.confirmOverwrite = true;
          continue;
        }
        throw new Error(typeof d === "string" ? d : d?.message || "Failed to approve");
      }
    } catch (e: any) { setError(e.message || "Approve failed"); }
    finally { setApproving(false); }
  }, [mergeRequestId, projectSlug, onMerged]);

  // Reject merge request (admin)
  const handleRejectMr = useCallback(async () => {
    if (!mergeRequestId) return;
    setRejecting(true); setError(null);
    try {
      const res = await authFetch(`/api/projects/${projectSlug}/studio/merge-requests/${mergeRequestId}/reject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to reject");
      }
      onMerged();
    } catch (e: any) { setError(e.message || "Reject failed"); }
    finally { setRejecting(false); }
  }, [mergeRequestId, projectSlug, onMerged]);

  // Close/withdraw merge request (owner only — backend enforces ownership)
  const handleCloseMr = useCallback(async () => {
    if (!mergeRequestId) return;
    setClosingMr(true); setError(null);
    try {
      const res = await authFetch(`/api/projects/${projectSlug}/studio/merge-requests/${mergeRequestId}/close`, {
        method: "POST",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to close");
      }
      onMerged();
    } catch (e: any) { setError(e.message || "Close failed"); }
    finally { setClosingMr(false); }
  }, [mergeRequestId, projectSlug, onMerged]);

  const hasConflicts = (preview?.conflicts?.length || 0) > 0;
  const ff = preview?.fastForward ?? false;
  const conflicts = preview?.conflicts || [];
  const isLoading = loading;
  const headsMatch = !loading && !!sourceCommitId && !!targetCommitId && sourceCommitId === targetCommitId;
  // In overwrite mode, never block — the whole point is to replace target regardless of ancestry
  const nothingToDo = mergeStrategy === "overwrite" ? false : (headsMatch || nothingToMerge);

  // Diff direction labels
  const beforeLabel = selectedCommitForDiff
    ? `Before ${selectedCommitForDiff.id.slice(0, 7)}`
    : compareFromFork && !rebasePreviewData && !ff ? `${targetBranchName} at the fork point` : targetBranchName;
  const afterLabel = selectedCommitForDiff ? `${selectedCommitForDiff.id.slice(0, 7)}` : branchName;

  // Footer buttons depend on context: MR review vs normal merge flow
  const renderFooter = () => {
    if (submitted) {
      return (
        <div className="flex items-center gap-2 px-5 py-3.5 border-t border-slate-200 bg-emerald-50/50 shrink-0">
          <Check className="size-4 text-emerald-500" />
          <span className="text-[12px] font-medium text-emerald-700">Merge request submitted — an admin will review and approve it.</span>
        </div>
      );
    }

    // Completed MR — read-only historical view
    if (isCompletedMr) {
      const statusLabel = mergeRequestStatus === "merged" ? "Merged" : mergeRequestStatus === "closed" ? "Closed" : "Rejected";
      const statusColor = mergeRequestStatus === "merged"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : mergeRequestStatus === "closed"
        ? "bg-slate-50 text-slate-600 border-slate-200"
        : "bg-red-50 text-red-700 border-red-200";
      const StatusIcon = mergeRequestStatus === "merged" ? GitMerge : X;
      return (
        <div className="flex items-center px-5 py-3 border-t border-slate-200 bg-slate-50/50 shrink-0">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${statusColor}`}>
            <StatusIcon className="size-3" />
            {statusLabel}
          </span>
        </div>
      );
    }

    // MR review mode — show approve/reject for admins, rebase for anyone
    if (isMrReview) {
      return (
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50/50 shrink-0">
          <div className="flex items-center gap-1.5 text-[10px] text-indigo-600">
            <Shield className="size-3" />
            <span>Merge request review</span>
          </div>
          <div className="flex items-center gap-2">
            {pinnedCopy && !ff && (
              <span className="text-[10px] text-amber-700 max-w-[420px]">
                {targetBranchName} has new commits since this request was filed. The requester can submit it again
                (Rebase &amp; Merge, Keep source branch unchanged).
              </span>
            )}
            {!ff && !rebasePreviewData && mergeStrategy !== "overwrite" && !pinnedCopy && (
              <>
                <button
                  onClick={handleResetToDraft}
                  disabled={resettingToDraft || rebasing || approving || rejecting}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {resettingToDraft ? "Resetting…" : "Reset to Draft"}
                </button>
                <button
                  onClick={handleRebasePreview}
                  disabled={rebasing || approving || rejecting || resettingToDraft}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {rebasing ? "Previewing…" : "Rebase"}
                </button>
              </>
            )}
            {rebasePreviewData && !rebaseCompleted && (
              <>
                <button
                  onClick={handleRebaseCancel}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancel Rebase
                </button>
                {stalePreview ? (
                  <button
                    onClick={handleRebasePreview}
                    disabled={rebasing}
                    className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {rebasing ? "Previewing…" : "Preview again"}
                  </button>
                ) : (
                  <button
                    onClick={handleRebaseApply}
                    disabled={rebasing || (rebasePreviewData?.conflicts?.length || 0) > 0}
                    title={(rebasePreviewData?.conflicts?.length || 0) > 0
                      ? "This rebase has conflicts. Resolve them in a rebase session (Actions → Rebase from…)."
                      : undefined}
                    className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {rebasing ? "Applying…" : "Confirm Rebase"}
                  </button>
                )}
              </>
            )}
            {mrPending && isAdmin && (
              <>
                <button
                  onClick={handleRejectMr}
                  disabled={rejecting || approving}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {rejecting ? "Rejecting…" : "Reject"}
                </button>
                <button
                  onClick={handleApproveMr}
                  disabled={approving || rejecting || (!ff && mergeStrategy !== "overwrite") || (hasConflicts && mergeStrategy !== "overwrite") || nothingToDo || hasUncommittedChanges}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {approving ? "Approving…" : "Approve & Merge"}
                </button>
              </>
            )}
            {mrPending && isOwner && (
              <button
                onClick={handleCloseMr}
                disabled={closingMr}
                className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {closingMr ? "Closing…" : "Close"}
              </button>
            )}
          </div>
        </div>
      );
    }

    // Rebase-only mode (from "Rebase from..." action)
    if (isRebaseMode) {
      return (
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50/50 shrink-0">
          <div />
          <div className="flex items-center gap-2">
            <button onClick={handleClose} className="px-3 py-1.5 text-[11px] font-medium text-slate-600 hover:text-slate-800 rounded-md hover:bg-slate-100 transition-colors">
              {rebaseCompleted || resetToDraftCompleted || alreadyUpToDate ? "Done" : "Cancel"}
            </button>
            {!rebaseCompleted && !resetToDraftCompleted && !alreadyUpToDate && !rebasePreviewData && (
              <button
                onClick={handleResetToDraft}
                disabled={resettingToDraft || rebasing}
                className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {resettingToDraft ? "Resetting…" : "Reset to Draft"}
              </button>
            )}
            {!rebaseCompleted && !resetToDraftCompleted && rebasePreviewData && stalePreview && (
              <button
                onClick={handleRebasePreview}
                disabled={rebasing}
                className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {rebasing ? "Previewing…" : "Preview again"}
              </button>
            )}
            {!rebaseCompleted && !resetToDraftCompleted && rebasePreviewData && !stalePreview && (
              <>
                <button
                  onClick={handleRebaseCancel}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  Cancel Rebase
                </button>
                <button
                  onClick={handleRebaseApply}
                  disabled={rebasing || (rebasePreviewData?.conflicts?.length || 0) > 0}
                  title={(rebasePreviewData?.conflicts?.length || 0) > 0
                    ? "This rebase has conflicts. Resolve them in a rebase session (Actions → Rebase from…)."
                    : undefined}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {rebasing ? "Applying…" : "Confirm Rebase"}
                </button>
              </>
            )}
            {!rebaseCompleted && !resetToDraftCompleted && !alreadyUpToDate && !rebasePreviewData && (
              <button
                onClick={handleRebasePreview}
                disabled={rebasing || resettingToDraft}
                className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {rebasing ? "Previewing…" : "Preview Rebase"}
              </button>
            )}
          </div>
        </div>
      );
    }

    // Nothing to do — just show Done button
    if (nothingToDo) {
      return (
        <div className="flex items-center justify-end px-5 py-3 border-t border-slate-200 bg-slate-50/50 shrink-0">
          <button onClick={handleClose} className="px-3 py-1.5 text-[11px] font-medium text-slate-600 hover:text-slate-800 rounded-md hover:bg-slate-100 transition-colors">
            Done
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 bg-slate-50/50 shrink-0">
        {/* Left side status */}
        {rebaseCompleted && (
          <div className="flex items-center gap-1.5 text-[10px] text-emerald-600">
            <Check className="size-3" />
            <span>Rebase completed</span>
          </div>
        )}
        {!rebaseCompleted && rebaseStrategy === "temp" && (
          <div className="flex items-center gap-1.5 text-[10px] text-indigo-600">
            <Check className="size-3" />
            <span>{pullMode ? "Pulling rebased copy" : "Source unchanged — merging rebased copy"}</span>
          </div>
        )}
        {!rebaseCompleted && !ff && rebasePreviewData && mergeStrategy !== "overwrite" && (
          <div className="flex items-center gap-1.5 text-[10px] text-amber-600">
            <AlertTriangle className="size-3" />
            <span>Rebase required</span>
          </div>
        )}
        {!rebaseCompleted && !ff && !rebasePreviewData && rebasing && mergeStrategy !== "overwrite" && (
          <div className="flex items-center gap-1.5 text-[10px] text-amber-600">
            <Loader2 className="size-3 animate-spin" />
            <span>Preparing rebase…</span>
          </div>
        )}
        {ff && isProtected && (
          <div className="flex items-center gap-1.5 text-[10px] text-amber-600">
            <Shield className="size-3" />
            <span>Protected branch — requires approval</span>
          </div>
        )}
        {ff && !isProtected && <div />}
        {mergeStrategy === "overwrite" && !rebaseCompleted && !rebaseStrategy && <div />}

        {/* Right side buttons */}
        <div className="flex items-center gap-2">
          <button onClick={handleClose} className="px-3 py-1.5 text-[11px] font-medium text-slate-600 hover:text-slate-800 rounded-md hover:bg-slate-100 transition-colors">
            Cancel
          </button>

          {/* Rebase & Merge button — shown when not fast-forward, delegates to RebaseSessionDialog */}
          {!ff && !rebasePreviewData && !rebaseCompleted && !rebasing && mergeStrategy !== "overwrite" && (
            <button
              onClick={onRebase || handleRebasePreview}
              disabled={!!draftWarning}
              className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Rebase & Merge
            </button>
          )}

          {/* Merge / Submit — shown when ready (ff, rebase completed, already up to date, or temp strategy chosen) */}
          {(ff || rebaseCompleted || alreadyUpToDate || rebaseStrategy === "temp" || mergeStrategy === "overwrite") && (
            <>
              {isProtected ? (
                <button
                  onClick={handleSubmitForReview}
                  disabled={submitting || hasUncommittedChanges || !!draftWarning}
                  className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? "Submitting…" : "Submit for Review"}
                </button>
              ) : (
                <button
                  onClick={handleDirectMerge}
                  disabled={merging || hasUncommittedChanges || !!draftWarning}
                  className={`px-3 py-1.5 text-[11px] font-medium rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-indigo-600 hover:bg-indigo-700`}
                >
                  {merging ? "Merging…" : mergeStrategy === "overwrite" ? "Overwrite" : "Merge"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-xl shadow-2xl border border-slate-200 w-[95vw] max-w-[1400px] h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200 shrink-0">
          <GitMerge className="size-4 text-indigo-500" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-slate-800">{isCompletedMr ? "Merge Summary" : isRebaseMode ? "Rebase Preview" : pullMode ? "Pull Preview" : "Merge Preview"}</div>
            <div className="text-[10px] text-slate-400 flex items-center gap-1 flex-wrap">
              <span className="font-medium text-slate-600">{branchName}</span>
              {sourceCommitId && <span className="font-mono text-slate-400">({sourceCommitId.slice(0, 7)})</span>}
              <span>→</span>
              <span className="font-medium text-slate-600">{targetBranchName}</span>
              {targetCommitId && <span className="font-mono text-slate-400">({targetCommitId.slice(0, 7)})</span>}
              {!isCompletedMr && ff && !isRebaseMode && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 font-medium">Fast-forward</span>}
              {!isCompletedMr && !ff && !rebaseCompleted && !isLoading && !isRebaseMode && !nothingToMerge && mergeStrategy !== "overwrite" && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600 font-medium">Rebase required</span>}
              {!isCompletedMr && rebaseCompleted && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 font-medium">Rebased</span>}
              {!isCompletedMr && alreadyUpToDate && mergeStrategy !== "overwrite" && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600 font-medium">Up to date</span>}
            </div>
          </div>
          {/* Strategy toggle — pill buttons */}
          {!isMrReview && !isRebaseMode && !pullMode && !isCompletedMr && (
            <div className="flex items-center rounded-md border border-slate-200 overflow-hidden shrink-0">
              <button
                onClick={() => setMergeStrategy("merge")}
                className={`px-3 py-1 text-[10px] font-medium transition-colors ${mergeStrategy === "merge" ? "bg-indigo-500 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              >
                Merge
              </button>
              <button
                onClick={() => setMergeStrategy("overwrite")}
                className={`px-3 py-1 text-[10px] font-medium transition-colors ${mergeStrategy === "overwrite" ? "bg-indigo-500 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
              >
                Overwrite
              </button>
            </div>
          )}
          <button onClick={handleClose} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
            <X className="size-4" />
          </button>
        </div>

        {/* Outdated MR banner — previous request was invalidated, starting fresh */}
        {isOutdatedMr && !submitted && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-700 flex items-center gap-2 shrink-0">
            <AlertTriangle className="size-3.5 text-amber-400" />
            <span>{outdatedReasonText(mergeRequestStatusReason, branchName, targetBranchName)}</span>
          </div>
        )}

        {/* Protected branch info banner */}
        {isProtected && !isMrReview && !isOutdatedMr && !isRebaseMode && !submitted && !isCompletedMr && (
          <div className="px-5 py-2 bg-indigo-50 border-b border-indigo-200 text-[11px] text-indigo-700 flex items-center gap-2 shrink-0">
            <Shield className="size-3.5 text-indigo-400" />
            <span><span className="font-medium">{targetBranchName}</span> is protected — this will create a merge request for admin review.</span>
          </div>
        )}

        {/* Conflict banner — hide when rebase preview is active or overwrite mode */}
        {hasConflicts && !rebasePreviewData && mergeStrategy !== "overwrite" && (
          <div className="border-b border-amber-200 bg-amber-50/80 shrink-0">
            <button onClick={() => setConflictsExpanded(p => !p)} className="flex items-center gap-2 w-full px-5 py-2 text-left">
              <AlertTriangle className="size-3.5 text-amber-500 shrink-0" />
              <span className="text-[11px] font-medium text-amber-800">{conflicts.length} conflict{conflicts.length > 1 ? "s" : ""} detected</span>
              {conflictsExpanded ? <ChevronDown className="size-3 text-amber-400 ml-auto" /> : <ChevronRight className="size-3 text-amber-400 ml-auto" />}
            </button>
            {conflictsExpanded && (
              <div className="px-5 pb-2 space-y-1">
                {conflicts.map((c: MergePreviewConflict) => (
                  <div key={c.slugPath} className="flex items-center gap-2 px-3 py-1.5 rounded bg-amber-100/60 text-[10px]">
                    <FileText className="size-3 text-amber-500 shrink-0" />
                    <span className="font-mono text-amber-800 truncate">{c.slugPath}</span>
                    <span className="text-amber-600 ml-auto shrink-0">{c.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Rebase success banner — only when the branch really is on top of
            the target now (it used to say so after applying a stale preview) */}
        {rebaseCompleted && ff && !headsMatch && !hasUncommittedChanges && mergeStrategy !== "overwrite" && (
          <div className="px-5 py-2 bg-emerald-50 border-b border-emerald-200 text-[11px] text-emerald-700 flex items-center gap-2 shrink-0">
            <Check className="size-3.5 text-emerald-500" />
            Rebase complete — branch is now up to date. Ready to merge.
          </div>
        )}
        {rebaseCompleted && !ff && !rebasePreviewData && mergeStrategy !== "overwrite" && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-700 flex items-center gap-2 shrink-0">
            <AlertTriangle className="size-3.5 text-amber-500" />
            Rebased, but {targetBranchName} has new commits since — rebase again to include them.
          </div>
        )}

        {/* Uncommitted changes banner */}
        {hasUncommittedChanges && mergeStrategy !== "overwrite" && (
          <div className="px-5 py-2 bg-indigo-50 border-b border-indigo-200 text-[11px] text-indigo-700 flex items-center gap-2 shrink-0">
            <GitCommit className="size-3.5 text-indigo-400" />
            Rebase complete — commit your changes before merging
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="px-5 py-2 bg-red-50 border-b border-red-200 text-[11px] text-red-700 flex items-center gap-2 shrink-0">
            <AlertTriangle className="size-3.5 text-red-400" />
            {error}
          </div>
        )}

        {/* Draft changes warning banner */}
        {draftWarning && !error && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-700 flex items-center gap-2 shrink-0">
            <AlertTriangle className="size-3.5 text-amber-400" />
            {draftWarning}
          </div>
        )}

        {/* Body — two-pane layout: commit chain left, diff right */}
        <div className="flex-1 min-h-0 flex overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="size-5 animate-spin mr-2" /> Loading merge preview…
            </div>
          ) : ((nothingToMerge || alreadyUpToDate) && mergeStrategy !== "overwrite") ? (
            <div className="flex-1 flex flex-col items-center justify-center py-16 text-slate-500">
              <Check className="size-8 text-emerald-400 mb-3" />
              <div className="text-[14px] font-medium text-slate-700">Already up to date</div>
              <div className="text-[11px] text-slate-400 mt-1">
                <span className="font-medium text-indigo-600">{branchName}</span> has no changes ahead of <span className="font-medium text-amber-600">{targetBranchName}</span>
              </div>
            </div>
          ) : (
            <>
              {/* Left panel — unified commit chain (always shown when not a completed MR) */}
              {!isCompletedMr && (() => {
                // When rebase preview is active, compute sections from its data
                const hasRebaseData = !!rebasePreviewData;
                const oldChain: any[] = rebasePreviewData?.oldCommits || [];
                const newChain: any[] = rebasePreviewData?.newCommits || [];
                const rebaseConflicts = rebasePreviewData?.conflicts || [];

                let rebaseSourceCommits: any[] = [];
                let rebaseOldSourceCommits: any[] = [];
                let rebaseTargetCommits: any[] = [];
                let rebaseCommonCommits: any[] = [];

                if (hasRebaseData) {
                  const oldIds = new Set(oldChain.map((c: any) => c.id));
                  const newIds = new Set(newChain.map((c: any) => c.id));
                  const forkId = rebasePreviewData?.forkPointId;
                  const newOnly = chainUntil(newChain, oldIds, forkId);
                  const oldOnly = chainUntil(oldChain, newIds, forkId);
                  const common = newChain.filter((c: any) => oldIds.has(c.id));
                  rebaseOldSourceCommits = oldOnly;
                  rebaseSourceCommits = newOnly.slice(0, oldOnly.length);
                  rebaseTargetCommits = newOnly.slice(oldOnly.length);
                  rebaseCommonCommits = common.slice(0, 5);
                }

                const srcCommits = mergeStrategy === "overwrite" ? commitChain.allSourceCommits : (hasRebaseData ? rebaseSourceCommits : commitChain.sourceCommits);
                const tgtCommits = mergeStrategy === "overwrite" ? [] : (hasRebaseData ? rebaseTargetCommits : commitChain.targetCommits);
                const cmnCommits = mergeStrategy === "overwrite" ? [] : (hasRebaseData ? rebaseCommonCommits : commitChain.commonCommits);
                const oldSrcCommits = mergeStrategy === "overwrite" ? [] : (hasRebaseData ? rebaseOldSourceCommits : []);
                const totalCount = srcCommits.length + tgtCommits.length + cmnCommits.length;
                const isUpToDate = alreadyUpToDate || (rebaseCompleted && headsMatch);

                return (
                  <div className="w-[280px] shrink-0 border-r border-slate-200 bg-slate-50/30 flex flex-col overflow-hidden">
                    {/* Panel header */}
                    {mergeStrategy === "overwrite" ? (
                      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-200 bg-white shrink-0">
                        <button
                          onClick={() => setOverwriteTab("source")}
                          className={`flex-1 px-3 py-1.5 text-[11px] font-semibold rounded-md transition-colors ${overwriteTab === "source" ? "bg-indigo-500 text-white shadow-sm" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                        >
                          Source
                        </button>
                        <button
                          onClick={() => setOverwriteTab("target")}
                          className={`flex-1 px-3 py-1.5 text-[11px] font-semibold rounded-md transition-colors ${overwriteTab === "target" ? "bg-amber-500 text-white shadow-sm" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                        >
                          Target
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-slate-200 bg-white shrink-0">
                        <GitCommit className="size-3.5 text-slate-500" />
                        <span className="text-[11px] font-semibold text-slate-700">Commits</span>
                      </div>
                    )}

                    <div className="flex-1 overflow-y-auto">
                      {/* ── Overwrite mode: Target tab ── */}
                      {mergeStrategy === "overwrite" && overwriteTab === "target" && (
                        <>
                          {commitChain.allTargetCommits.length > 0 ? (
                            <div className="pt-2 pb-1">
                              <div className="flex items-center gap-2 px-3 mb-1.5">
                                <span className="text-[9px] font-semibold uppercase tracking-wider text-amber-600">Target</span>
                                <span className="text-[9px] text-amber-400">{targetBranchName}</span>
                                <span className="flex-1 h-px bg-amber-100/60" />
                              </div>
                              <div className="relative ml-5">
                                <div className="absolute left-0 top-0 bottom-0 w-px bg-amber-200/70" />
                                {commitChain.allTargetCommits.map((c: any, i: number) => (
                                  <div key={`ot-${c.id}`} className="relative pl-4 pb-1">
                                    <div className="absolute left-[-3px] top-[10px] size-[7px] rounded-full ring-2 ring-slate-50 z-10 bg-amber-400" />
                                    <div
                                      onClick={() => c.parentId && setSelectedCommitForDiff({ id: c.id, parentId: c.parentId, message: c.message, branchOverride: propTargetBranchId || preview?.targetBranchId })}
                                      className={`mr-2 rounded px-2 py-1.5 cursor-pointer transition-all ${selectedCommitForDiff?.id === c.id ? "bg-amber-50 ring-1 ring-amber-300" : "hover:bg-white"}`}
                                    >
                                      <div className="flex items-center">
                                        {c.sequence != null && <span className="font-mono text-[9px] text-amber-400 w-[22px] shrink-0 text-right tabular-nums">#{c.sequence}</span>}
                                        <span className={`font-mono text-[10px] w-[52px] shrink-0 ml-1 ${selectedCommitForDiff?.id === c.id ? "text-amber-700" : "text-amber-600"}`}>{c.id.slice(0, 7)}</span>
                                        <span className="w-[36px] shrink-0 ml-0.5 flex justify-center">
                                          {i === 0 && <span className="text-[7px] font-bold uppercase px-1 py-px rounded-full bg-amber-100 text-amber-600 leading-none">HEAD</span>}
                                        </span>
                                        <span className="text-[9px] text-slate-600 truncate ml-0.5">{c.message || "(no msg)"}</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center py-8 text-[11px] text-slate-400 italic">
                              No target commits
                            </div>
                          )}
                        </>
                      )}

                      {/* ── Normal mode / Overwrite source tab ── */}
                      {(mergeStrategy !== "overwrite" || overwriteTab === "source") && (<>
                      {/* ── Source commits section ── */}
                      {srcCommits.length > 0 && (
                        <div className="pt-2 pb-1">
                          <div className="flex items-center gap-2 px-3 mb-1.5">
                            <span className="text-[9px] font-semibold uppercase tracking-wider text-indigo-600">Source</span>
                            <span className="text-[9px] text-indigo-400">{branchName}</span>
                            <span className="flex-1 h-px bg-indigo-100/60" />
                          </div>
                          <div className="relative ml-5">
                            <div className="absolute left-0 top-0 bottom-0 w-px bg-indigo-200/70" />
                            {srcCommits.map((c: any, i: number) => {
                              const isLast = i === srcCommits.length - 1;
                              const hasRebaseConflicts = rebaseConflicts.length > 0;
                              const brOverride = hasRebaseData ? rebasePreviewData?.tempBranchId : undefined;
                              return (
                                <div key={`s-${c.id}`} className={`relative pl-4 ${isLast ? "pb-1" : "pb-2"}`}>
                                  <div className={`absolute left-[-3px] top-[10px] size-[7px] rounded-full ring-2 ring-slate-50 z-10 bg-indigo-500`} />
                                  <div className={`mr-2 ${hasRebaseData && oldSrcCommits[i] ? (hasRebaseConflicts && i === srcCommits.length - 1 ? "border-l-2 border-l-red-400 rounded-sm" : "border-l-2 border-l-emerald-400 rounded-sm") : ""}`}>
                                    <div
                                      onClick={() => c.parentId && setSelectedCommitForDiff({ id: c.id, parentId: c.parentId, message: c.message, branchOverride: brOverride })}
                                      className={`rounded-r px-2 py-1 cursor-pointer transition-all ${selectedCommitForDiff?.id === c.id ? "bg-indigo-50 ring-1 ring-indigo-300" : "hover:bg-indigo-50/60"}`}
                                    >
                                      <div className="flex items-center">
                                        {c.sequence != null && <span className="font-mono text-[9px] text-indigo-400 w-[22px] shrink-0 text-right tabular-nums">#{c.sequence}</span>}
                                        <span className={`font-mono text-[10px] w-[52px] shrink-0 ml-1 ${selectedCommitForDiff?.id === c.id ? "text-indigo-600" : "text-indigo-500"}`}>{c.id.slice(0, 7)}</span>
                                        <span className="w-[36px] shrink-0 ml-0.5 flex justify-center">
                                          {i === 0 && <span className="text-[7px] font-bold uppercase px-1 py-px rounded-full bg-indigo-100 text-indigo-600 leading-none">HEAD</span>}
                                        </span>
                                        <span className="text-[9px] text-slate-600 truncate ml-0.5">{c.message || "(no msg)"}</span>
                                      </div>
                                    </div>
                                    {hasRebaseData && oldSrcCommits[i] && (
                                      <div
                                        onClick={() => oldSrcCommits[i].parentId && setSelectedCommitForDiff({ id: oldSrcCommits[i].id, parentId: oldSrcCommits[i].parentId, message: oldSrcCommits[i].message, branchOverride: branchId })}
                                        className={`mt-px rounded-r px-2 py-1 cursor-pointer transition-all ${selectedCommitForDiff?.id === oldSrcCommits[i].id ? "bg-indigo-50 ring-1 ring-indigo-300" : "hover:bg-white"}`}
                                      >
                                        <div className="flex items-center">
                                          {c.sequence != null && <span className="w-[22px] shrink-0" />}
                                          <span className={`font-mono text-[10px] w-[52px] shrink-0 ml-1 ${selectedCommitForDiff?.id === oldSrcCommits[i].id ? "text-indigo-600" : "text-slate-400"}`}>{oldSrcCommits[i].id.slice(0, 7)}</span>
                                          <span className="w-[36px] shrink-0 flex justify-center ml-0.5">
                                            <span className="text-[7px] font-semibold uppercase px-1 py-px rounded-full bg-slate-100 text-slate-500 leading-none">old</span>
                                          </span>
                                          <span className="text-[9px] text-slate-500 truncate ml-0.5">{oldSrcCommits[i].message || "(no msg)"}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* ── Target commits section ── */}
                      {tgtCommits.length > 0 && (
                        <div className="pt-2 pb-1">
                          <div className="flex items-center gap-2 px-3 mb-1.5">
                            <span className="text-[9px] font-semibold uppercase tracking-wider text-amber-600">Target</span>
                            <span className="text-[9px] text-amber-400">{targetBranchName}</span>
                            <span className="flex-1 h-px bg-amber-100/60" />
                          </div>
                          <div className="relative ml-5">
                            <div className="absolute left-0 top-0 bottom-0 w-px bg-amber-200/70" />
                            {tgtCommits.map((c: any) => {
                              return (
                                <div key={`t-${c.id}`} className="relative pl-4 pb-1">
                                  <div className="absolute left-[-3px] top-[10px] size-[7px] rounded-full ring-2 ring-slate-50 z-10 bg-amber-400" />
                                  <div
                                    onClick={() => c.parentId && setSelectedCommitForDiff({ id: c.id, parentId: c.parentId, message: c.message })}
                                    className={`mr-2 rounded px-2 py-1.5 cursor-pointer transition-all ${selectedCommitForDiff?.id === c.id ? "bg-indigo-50 ring-1 ring-indigo-300" : "hover:bg-white"}`}
                                  >
                                    <div className="flex items-center">
                                      {c.sequence != null && <span className="font-mono text-[9px] text-amber-400 w-[22px] shrink-0 text-right tabular-nums">#{c.sequence}</span>}
                                      <span className={`font-mono text-[10px] w-[52px] shrink-0 ml-1 ${selectedCommitForDiff?.id === c.id ? "text-indigo-600" : "text-amber-600"}`}>{c.id.slice(0, 7)}</span>
                                      <span className="w-[36px] shrink-0 ml-0.5" />
                                      <span className="text-[9px] text-slate-600 truncate ml-0.5">{c.message || "(no message)"}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* ── Common ancestors section ── */}
                      {cmnCommits.length > 0 && (
                        <div className="pt-2 pb-2">
                          <div className="flex items-center gap-2 px-3 mb-1.5">
                            <span className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Common</span>
                            <span className="flex-1 h-px bg-slate-100" />
                          </div>
                          <div className="relative ml-5">
                            <div className="absolute left-0 top-0 bottom-0 w-px bg-slate-200/70" />
                            {cmnCommits.map((c: any) => (
                              <div key={`a-${c.id}`} className="relative pl-4 pb-1">
                                <div className="absolute left-[-3px] top-[10px] size-[5px] rounded-full bg-slate-300 ring-2 ring-slate-50 z-10" />
                                <div
                                  onClick={() => c.parentId && setSelectedCommitForDiff({ id: c.id, parentId: c.parentId, message: c.message })}
                                  className={`mr-2 rounded px-2 py-1.5 cursor-pointer transition-all ${selectedCommitForDiff?.id === c.id ? "bg-indigo-50 ring-1 ring-indigo-300" : "hover:bg-white"}`}
                                >
                                  <div className="flex items-center">
                                    {c.sequence != null && <span className="font-mono text-[9px] text-slate-300 w-[22px] shrink-0 text-right tabular-nums">#{c.sequence}</span>}
                                    <span className={`font-mono text-[10px] w-[52px] shrink-0 ml-1 ${selectedCommitForDiff?.id === c.id ? "text-indigo-600" : "text-slate-400"}`}>{c.id.slice(0, 7)}</span>
                                    <span className="w-[36px] shrink-0 ml-0.5" />
                                    <span className={`text-[9px] truncate ml-0.5 ${selectedCommitForDiff?.id === c.id ? "text-slate-700" : "text-slate-400"}`}>{c.message || "(no message)"}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Empty state */}
                      {totalCount === 0 && !isUpToDate && (
                        <div className="flex items-center justify-center py-8 text-[11px] text-slate-400 italic">
                          No commits to show
                        </div>
                      )}
                      </>)}
                    </div>

                    {/* Summary footer */}
                    <div className="shrink-0 border-t border-slate-200 bg-white px-3 py-2">
                      {mergeStrategy === "overwrite" ? (
                        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-indigo-50 border border-indigo-100">
                          <GitMerge className="size-3 text-indigo-500 shrink-0" />
                          <span className="text-[10px] text-indigo-700 font-medium">
                            {commitChain.allSourceCommits.length} commit{commitChain.allSourceCommits.length !== 1 ? "s" : ""} — will replace target
                          </span>
                        </div>
                      ) : isUpToDate ? (
                        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-emerald-50 border border-emerald-100">
                          <Check className="size-3 text-emerald-500 shrink-0" />
                          <span className="text-[10px] text-emerald-700 font-medium">
                            {rebaseCompleted ? "Rebased — up to date" : "Already up to date — nothing to rebase"}
                          </span>
                        </div>
                      ) : rebaseConflicts.length > 0 ? (
                        (() => {
                          // Order conflicts come back as a separate kind so
                          // the user can see them broken out in the count.
                          // file conflicts get resolved via the file diff
                          // panel; order conflicts via the per-file
                          // ChildrenOrderResolver in RebaseSessionDialog.
                          const orderCount = rebaseConflicts.filter(
                            (c: any) => c.kind === "children_order" || c.reason === "children_order_diverged",
                          ).length;
                          const fileCount = rebaseConflicts.length - orderCount;
                          const parts: string[] = [];
                          if (fileCount > 0) parts.push(`${fileCount} file conflict${fileCount === 1 ? "" : "s"}`);
                          if (orderCount > 0) parts.push(`${orderCount} order conflict${orderCount === 1 ? "" : "s"}`);
                          return (
                            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-amber-50 border border-amber-200">
                              <AlertTriangle className="size-3 text-amber-500 shrink-0" />
                              <span className="text-[10px] text-amber-800 font-medium">
                                {parts.join(" · ")}
                              </span>
                            </div>
                          );
                        })()
                      ) : hasRebaseData ? (
                        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-emerald-50 border border-emerald-100">
                          <Check className="size-3 text-emerald-500 shrink-0" />
                          <span className="text-[10px] text-emerald-700 font-medium">
                            {srcCommits.length} commit{srcCommits.length !== 1 ? "s" : ""} replayed cleanly
                          </span>
                        </div>
                      ) : ff && !isRebaseMode ? (
                        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-indigo-50 border border-indigo-100">
                          <GitMerge className="size-3 text-indigo-500 shrink-0" />
                          <span className="text-[10px] text-indigo-700 font-medium">
                            {srcCommits.length} commit{srcCommits.length !== 1 ? "s" : ""} to merge
                          </span>
                        </div>
                      ) : !ff && !hasRebaseData ? (
                        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-amber-50 border border-amber-100">
                          <AlertTriangle className="size-3 text-amber-400 shrink-0" />
                          <span className="text-[10px] text-amber-700 font-medium">Rebase required</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })()}

              {/* Right panel — diff view */}
              <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                {/* Diff context header — always visible so layout stays stable */}
                <div className="px-5 py-1.5 min-h-[34px] border-b border-indigo-100 bg-indigo-50/30 flex items-center gap-2 shrink-0">
                  {selectedCommitForDiff ? (
                    <>
                      <span className="text-[10px] text-indigo-600">
                        Commit <span className="font-mono font-medium">{selectedCommitForDiff.id.slice(0, 7)}</span>
                        {" — "}{selectedCommitForDiff.message || "(no message)"}
                      </span>
                      <button onClick={() => setSelectedCommitForDiff(null)} className="ml-auto shrink-0 px-2.5 py-1 rounded-md text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors">
                        Branch compare
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="text-[10px] text-indigo-600">
                        {compareFromFork && !rebasePreviewData && !ff
                          ? <>Branch changes — <span className="font-medium">{branchName}</span> since it forked from <span className="font-medium">{targetBranchName}</span>{forkPointId && <span className="font-mono"> ({forkPointId.slice(0, 7)})</span>}</>
                          : <>Branch compare — <span className="font-medium">{branchName}</span> vs <span className="font-medium">{targetBranchName}</span></>}
                      </span>
                      {forkPointId && !ff && !rebasePreviewData && !isCompletedMr && !propTargetCommitId && (
                        <button
                          onClick={() => setCompareMode(m => m === "fork" ? "heads" : "fork")}
                          className="ml-auto shrink-0 px-2.5 py-1 rounded-md text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors"
                        >
                          {compareMode === "fork" ? `Compare with ${targetBranchName} head` : "Show branch changes"}
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Tree diff — delegated to CommitDiffPanel */}
                {diffPanelCommitB ? (
                  <CommitDiffPanel
                    commitA={diffPanelCommitA}
                    commitB={diffPanelCommitB}
                    projectSlug={projectSlug}
                    branchId={diffPanelBranchId}
                    beforeLabel={beforeLabel}
                    afterLabel={afterLabel}
                    conflictPaths={(rebasePreviewData?.conflicts || preview?.conflicts || [])
                      .filter((c: any) => c.kind !== "children_order" && c.reason !== "children_order_diverged")
                      .map((c: any) => c.slugPath)}
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center py-16 text-[12px] text-slate-400">
                    No commits to compare.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {renderFooter()}
      </div>
    </div>,
    document.body
  );
}
